import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ActiveSession } from '../../data/local';
import { practiceFingerprintFromLaunchSearch } from '../quiz/prepareQuizRuntimeLaunch';
import {
  findIncompletePracticeSession,
  resolveIncompletePracticeDisposition,
  withQuizSessionId,
  type PracticeLaunchFingerprint,
} from '../quiz/quizSessionRuntime';

export type IncompleteTrainingCardModel = {
  sessionId: string;
  scopeLabel: string;
  totalQuestions: number;
  answeredCount: number;
  continueHref: string;
  freshHref: string;
};

function answeredCountOf(session: ActiveSession): number {
  return session.userAnswers.filter(
    (answer) => String(answer.answer ?? '').trim().length > 0
  ).length;
}

function scopeLabelOf(session: ActiveSession): string {
  if (session.platformScope) {
    return `${session.platformScope.languageName} · ${session.platformScope.examName}`;
  }
  return session.examType.toUpperCase();
}

function withSessionIdOnHref(href: string, sessionId: string): string {
  const qIndex = href.indexOf('?');
  const path = qIndex >= 0 ? href.slice(0, qIndex) : href;
  const query = qIndex >= 0 ? href.slice(qIndex + 1) : '';
  const params = withQuizSessionId(new URLSearchParams(query), sessionId);
  return `${path}?${params.toString()}`;
}

/** Pure helper for tests — build card model from session + hrefs. */
export function buildIncompleteTrainingCardModel(
  session: ActiveSession,
  startHref: string
): IncompleteTrainingCardModel {
  return {
    sessionId: session.id,
    scopeLabel: scopeLabelOf(session),
    totalQuestions: session.questionIds.length,
    answeredCount: answeredCountOf(session),
    continueHref: withSessionIdOnHref(startHref, session.id),
    freshHref: startHref,
  };
}

/**
 * Choice Training only: surface a matching incomplete local Session for the training entry list.
 * Writing uses silent_continue — never returns a card.
 */
export function useIncompleteTrainingCard(input: {
  skillId: string | null | undefined;
  startHref: string | null | undefined;
}): {
  incomplete: IncompleteTrainingCardModel | null;
  status: 'loading' | 'ready';
  reload: () => Promise<void>;
  clear: () => void;
} {
  const skillId = input.skillId?.trim() || null;
  const startHref = input.startHref?.trim() || null;
  const showPrompt =
    skillId != null &&
    resolveIncompletePracticeDisposition({ skillId }) === 'show_prompt';

  const fingerprint: PracticeLaunchFingerprint | null = useMemo(() => {
    if (!showPrompt || !startHref) return null;
    return practiceFingerprintFromLaunchSearch(startHref);
  }, [showPrompt, startHref]);

  const [incomplete, setIncomplete] = useState<IncompleteTrainingCardModel | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready'>('loading');

  const reload = useCallback(async () => {
    if (!showPrompt || !fingerprint || !startHref) {
      setIncomplete(null);
      setStatus('ready');
      return;
    }
    setStatus('loading');
    try {
      const session = await findIncompletePracticeSession(fingerprint);
      if (!session) {
        setIncomplete(null);
        return;
      }
      setIncomplete(buildIncompleteTrainingCardModel(session, startHref));
    } catch {
      setIncomplete(null);
    } finally {
      setStatus('ready');
    }
  }, [fingerprint, showPrompt, startHref]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const clear = useCallback(() => {
    setIncomplete(null);
  }, []);

  return { incomplete, status, reload, clear };
}
